import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DataTable } from '@/components/DataTable';
import { Select } from '@/components/Select';
import { Modal } from '@/components/Modal';
import { getTrackerConversions, updateConversionPayout } from '@/api/tracker-stats';
import { useCanEdit } from '@/components/EditOnly';
import { getCampaigns } from '@/api/tracker-campaigns';
import { getFlows } from '@/api/tracker-flows';
import { CountryMultiSelect } from '@/components/CountryMultiSelect';
import { GoogleAdsCell, GoogleAdsDetails, GOOGLE_ADS_FILTER_OPTIONS } from '@/components/tracker/GoogleAdsAttribution';
import { AsaCell, AsaDetails, ASA_FILTER_OPTIONS } from '@/components/tracker/AsaAttribution';
import type { ConversionRow } from '@/api/tracker-types';
import { formatDateTime } from '@/lib/datetime';
import { useAuthStore } from '@/store/auth';

function fmtCents(c: number) {
  return c ? `$${(c / 100).toFixed(2)}` : '$0.00';
}
function shortId(id: string) {
  return id.length > 12 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;
}

export function TrackerConversions() {
  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);
  const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
  // QA only ever sees the current day. The API clamps the range too — this just
  // keeps the filters honest instead of showing a range that silently won't apply.
  const isQa = useAuthStore((s) => s.isQa());
  const [from, setFrom] = useState(isQa ? todayStr : weekAgo.toISOString().slice(0, 10));
  const [to, setTo] = useState(todayStr);
  const [campaignId, setCampaignId] = useState('');
  const [labelFilter, setLabelFilter] = useState('');
  const [flowId, setFlowId] = useState('');
  const [countries, setCountries] = useState<string[]>([]);
  const [status, setStatus] = useState<'' | 'reg' | 'dep'>('');
  const [search, setSearch] = useState('');
  const [googleAds, setGoogleAds] = useState<'' | '1' | '0'>('');
  const [asa, setAsa] = useState<'' | '1' | '0'>('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<ConversionRow | null>(null);
  const [editingPayout, setEditingPayout] = useState(false);
  const [payoutInput, setPayoutInput] = useState('');
  const [payoutSaving, setPayoutSaving] = useState(false);
  const [payoutError, setPayoutError] = useState<string | null>(null);
  const canEdit = useCanEdit();
  const limit = 50;

  const selectedRef = useRef<ConversionRow | null>(null);
  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  const { data: campaigns = [] } = useQuery({ queryKey: ['tracker-campaigns'], queryFn: getCampaigns });
  const { data: flows = [] } = useQuery({
    queryKey: ['tracker-flows', campaignId],
    queryFn: () => getFlows(campaignId),
    enabled: !!campaignId,
  });

  const campaignName = (id: string | null) => campaigns.find((c) => c.id === id)?.name || (id ? shortId(id) : '—');
  const flowName = (id: string | null) => flows.find((f) => f.id === id)?.name || (id ? shortId(id) : '—');

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['tracker-conversions', from, to, campaignId, labelFilter, flowId, countries, status, search, googleAds, asa, page],
    queryFn: () => getTrackerConversions({
      from: from ? `${from} 00:00:00` : undefined,
      to: to ? `${to} 23:59:59` : undefined,
      campaign_id: campaignId || undefined,
      label: labelFilter || undefined,
      flow_id: flowId || undefined,
      countries: countries.length ? countries : undefined,
      status: status || undefined,
      search: search || undefined,
      google_ads: googleAds || undefined,
      asa: asa || undefined,
      page,
      limit,
    }),
  });

  const openConversion = (r: ConversionRow | null) => {
    setSelected(r);
    setEditingPayout(false);
    setPayoutError(null);
  };

  const startPayoutEdit = () => {
    if (!selected) return;
    setPayoutInput((selected.payout_cents / 100).toFixed(2));
    setPayoutError(null);
    setEditingPayout(true);
  };

  const cancelPayoutEdit = () => {
    setEditingPayout(false);
    setPayoutError(null);
  };

  const savePayout = async () => {
    if (!selected || payoutSaving) return;
    const value = Number(payoutInput);
    if (payoutInput.trim() === '' || !Number.isFinite(value) || value < 0) {
      setPayoutError('Введите неотрицательное число');
      return;
    }
    const cents = Math.round(value * 100);
    const targetId = selected.id;
    setPayoutSaving(true);
    setPayoutError(null);
    try {
      const upd = await updateConversionPayout(targetId, cents);
      refetch();
      if (selectedRef.current?.id === targetId) {
        setSelected({ ...selectedRef.current, ...upd });
        setEditingPayout(false);
      }
    } catch (e: any) {
      if (selectedRef.current?.id === targetId) {
        setPayoutError(e?.message || 'Не удалось сохранить');
      }
    } finally {
      setPayoutSaving(false);
    }
  };

  const rows = data?.rows || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const labelValues = useMemo(
    () => Array.from(new Set(campaigns.map((c) => c.label).filter((l): l is string => !!l))).sort(),
    [campaigns]
  );
  const labelOptions = useMemo(() => [
    { value: '', label: 'All labels' },
    ...labelValues.map((l) => ({ value: l, label: l })),
  ], [labelValues]);
  const campaignOptions = useMemo(() => [
    { value: '', label: 'All campaigns' },
    ...campaigns
      .filter((c) => !labelFilter || c.label === labelFilter)
      .map((c) => ({ value: c.id, label: c.name })),
  ], [campaigns, labelFilter]);

  const flowOptions = useMemo(() => [
    { value: '', label: 'All flows' },
    ...flows.map((f) => ({ value: f.id, label: f.name })),
  ], [flows]);

  const statusOptions = [
    { value: '', label: 'All conversions' },
    { value: 'reg', label: 'Registrations' },
    { value: 'dep', label: 'Deposits' },
  ];

  const columns = [
    {
      key: 'created_at', label: 'Time',
      render: (r: ConversionRow) => (
        <span className="text-xs text-content-muted whitespace-nowrap">
          {formatDateTime(r.created_at)}
        </span>
      ),
    },
    {
      key: 'status', label: 'Status',
      render: (r: ConversionRow) => (
        <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${r.status === 'dep' ? 'bg-success-soft text-success' : 'bg-info-soft text-info'}`}>
          {r.status}
        </span>
      ),
    },
    {
      key: 'click_id', label: 'Click ID',
      render: (r: ConversionRow) => (
        <span className="font-mono text-xs text-content-muted" title={r.click_id}>{shortId(r.click_id)}</span>
      ),
    },
    {
      key: 'campaign', label: 'Campaign',
      render: (r: ConversionRow) => <span className="text-xs">{campaignName(r.tracker_campaign_id)}</span>,
    },
    {
      key: 'flow', label: 'Flow',
      render: (r: ConversionRow) => <span className="text-xs">{flowName(r.flow_id)}</span>,
    },
    {
      key: 'country', label: 'Country',
      render: (r: ConversionRow) => <span className="text-xs">{r.country || r.clk_country || '—'}</span>,
    },
    {
      key: 'device', label: 'Device',
      render: (r: ConversionRow) => (
        <span className="text-xs text-content-muted">
          {[r.clk_device_type, r.clk_os].filter(Boolean).join(' / ') || r.platform || '—'}
        </span>
      ),
    },
    {
      key: 'payout', label: 'Payout',
      render: (r: ConversionRow) => (
        <span className={`text-xs ${r.payout_cents ? 'font-medium text-content' : 'text-content-subtle'}`}>
          {fmtCents(r.payout_cents)}
          {r.payout_edited_at && <span title="Выплата правлена вручную" className="text-warning"> *</span>}
        </span>
      ),
    },
    {
      key: 'source', label: 'Source',
      render: (r: ConversionRow) => {
        const t = r.clk_source_type;
        if (!t) return <span className="text-xs text-content-subtle">—</span>;
        const cls =
          t === 'ad_click'        ? 'bg-info-soft text-info' :
          t === 'referral_click'  ? 'bg-primary/10 text-primary' :
          t === 'referral_force'  ? 'bg-fuchsia-100 text-fuchsia-800' :
          t === 'organic_force'   ? 'bg-warning-soft text-warning' :
          t === 'playable'        ? 'bg-success-soft text-success' :
                                    'bg-surface-muted text-content-muted';
        return (
          <div className="flex flex-col gap-0.5">
            <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium w-fit ${cls}`}>{t}</span>
            {r.clk_source_bundle_id && (
              <span className="text-[11px] text-content-subtle font-mono truncate max-w-[180px]" title={r.clk_source_bundle_id}>
                {r.clk_source_bundle_id}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'google_ads', label: 'Google Ads',
      render: (r: ConversionRow) => <GoogleAdsCell sub3={r.clk_sub_id_3} sub4={r.clk_sub_id_4} />,
    },
    {
      key: 'asa', label: 'ASA',
      render: (r: ConversionRow) => <AsaCell isAsa={r.clk_is_asa} campaignId={r.clk_asa_campaign_id} adGroupId={r.clk_asa_adgroup_id} keywordId={r.clk_asa_keyword_id} />,
    },
    {
      key: 'sub_id_1', label: 'sub_id_1',
      render: (r: ConversionRow) => (
        <span className="text-xs text-content-muted font-mono">{r.clk_sub_id_1 || '—'}</span>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Tracker Conversions</h1>
          <p className="text-sm text-content-subtle mt-1">Registrations and deposits received via postback</p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="text-sm px-3 py-1.5 border border-border-strong rounded-md hover:bg-surface-muted disabled:opacity-50"
        >
          {isFetching ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-4 gap-4 mb-4">
        <div className="bg-surface border border-border rounded-lg p-4">
          <p className="text-sm text-content-subtle">Total</p>
          <p className="text-xl sm:text-2xl font-semibold text-content">{(data?.total || 0).toLocaleString()}</p>
        </div>
        <div className="bg-surface border border-border rounded-lg p-4">
          <p className="text-sm text-content-subtle">Registrations</p>
          <p className="text-2xl font-semibold text-info">{(data?.regs || 0).toLocaleString()}</p>
        </div>
        <div className="bg-surface border border-border rounded-lg p-4">
          <p className="text-sm text-content-subtle">Deposits</p>
          <p className="text-2xl font-semibold text-success">{(data?.deps || 0).toLocaleString()}</p>
        </div>
        <div className="bg-surface border border-border rounded-lg p-4">
          <p className="text-sm text-content-subtle">Payout</p>
          <p className="text-xl sm:text-2xl font-semibold text-content">{fmtCents(data?.payout_cents || 0)}</p>
        </div>
      </div>

      <div className="bg-surface border border-border rounded-lg p-4 mb-4 grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <div>
          <label className="block text-xs text-content-subtle mb-1">From</label>
          <input type="date" value={from} disabled={isQa} title={isQa ? 'QA: только текущий день' : undefined} onChange={(e) => { setFrom(e.target.value); setPage(1); }} className="w-full px-3 py-2 border border-border rounded-lg text-sm disabled:opacity-60" />
        </div>
        <div>
          <label className="block text-xs text-content-subtle mb-1">To</label>
          <input type="date" value={to} disabled={isQa} title={isQa ? 'QA: только текущий день' : undefined} onChange={(e) => { setTo(e.target.value); setPage(1); }} className="w-full px-3 py-2 border border-border rounded-lg text-sm disabled:opacity-60" />
        </div>
        <div>
          <label className="block text-xs text-content-subtle mb-1">Label</label>
          <Select
            value={labelFilter}
            options={labelOptions}
            onChange={(v) => {
              setLabelFilter(v);
              setPage(1);
              if (v && campaignId) {
                const c = campaigns.find((x) => x.id === campaignId);
                if (!c || c.label !== v) { setCampaignId(''); setFlowId(''); }
              }
            }}
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-xs text-content-subtle mb-1">Campaign</label>
          <Select value={campaignId} options={campaignOptions} onChange={(v) => { setCampaignId(v); setFlowId(''); setPage(1); }} className="w-full" />
        </div>
        <div>
          <label className="block text-xs text-content-subtle mb-1">Flow</label>
          <Select value={flowId} options={flowOptions} onChange={(v) => { setFlowId(v); setPage(1); }} className="w-full" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs text-content-subtle mb-1">Countries</label>
          <CountryMultiSelect value={countries} onChange={(v) => { setCountries(v); setPage(1); }} placeholder="All countries" size="sm" />
        </div>
        <div>
          <label className="block text-xs text-content-subtle mb-1">Status</label>
          <Select value={status} options={statusOptions} onChange={(v) => { setStatus(v as typeof status); setPage(1); }} className="w-full" />
        </div>
        <div>
          <label className="block text-xs text-content-subtle mb-1">Google Ads</label>
          <Select
            value={googleAds}
            options={GOOGLE_ADS_FILTER_OPTIONS}
            onChange={(v) => { setGoogleAds(v as '' | '1' | '0'); setPage(1); }}
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-xs text-content-subtle mb-1">Apple Search Ads</label>
          <Select
            value={asa}
            options={ASA_FILTER_OPTIONS}
            onChange={(v) => { setAsa(v as '' | '1' | '0'); setPage(1); }}
            className="w-full"
          />
        </div>
        <div className="md:col-span-4 lg:col-span-6">
          <label className="block text-xs text-content-subtle mb-1">Search (click_id or gclid prefix)</label>
          <input type="text" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="paste click_id…" className="w-full px-3 py-2 border border-border rounded-lg text-sm font-mono" />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={rows}
        loading={isLoading}
        keyExtractor={(r) => r.id}
        onRowClick={(r) => openConversion(r)}
      />

      <div className="mt-4 flex items-center justify-between text-sm text-content-muted">
        <div>
          {total.toLocaleString()} conversion{total === 1 ? '' : 's'} • page {page} / {totalPages}
        </div>
        <div className="flex gap-2">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="px-3 py-1.5 border border-border-strong rounded-md disabled:opacity-50 hover:bg-surface-muted">Prev</button>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="px-3 py-1.5 border border-border-strong rounded-md disabled:opacity-50 hover:bg-surface-muted">Next</button>
        </div>
      </div>

      <Modal isOpen={!!selected} onClose={() => openConversion(null)} title="Conversion details" size="lg">
        {selected && (
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2">
              <span className={`px-2 py-1 rounded text-xs font-medium ${selected.status === 'dep' ? 'bg-success-soft text-success' : 'bg-info-soft text-info'}`}>
                {selected.status}
              </span>
              {editingPayout ? (
                <span className="flex items-center gap-1.5">
                  <input
                    type="number" step="0.01" min="0" autoFocus
                    value={payoutInput}
                    onChange={(e) => setPayoutInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') savePayout();
                      if (e.key === 'Escape') cancelPayoutEdit();
                    }}
                    disabled={payoutSaving}
                    className="w-28 px-2 py-1 border border-border-strong rounded-md text-sm disabled:opacity-50"
                  />
                  <span className="text-sm text-content-muted">{selected.currency}</span>
                  <button onClick={savePayout} disabled={payoutSaving} className="text-xs px-2 py-1 rounded bg-primary text-white disabled:opacity-50">Save</button>
                  <button onClick={cancelPayoutEdit} disabled={payoutSaving} className="text-xs px-2 py-1 rounded border border-border-strong hover:bg-surface-muted">Cancel</button>
                </span>
              ) : (
                <span className="flex items-center gap-1.5">
                  <span className="text-sm font-medium text-content">{fmtCents(selected.payout_cents)} {selected.currency}</span>
                  {canEdit && (
                    <button onClick={startPayoutEdit} title="Править выплату" className="text-content-subtle hover:text-content text-sm leading-none">✎</button>
                  )}
                </span>
              )}
            </div>
            {payoutError && <div className="text-xs text-danger">{payoutError}</div>}
            {selected.payout_edited_at && (
              <div className="text-xs text-content-subtle">
                изначально {fmtCents(selected.original_payout_cents ?? 0)} · правлено {formatDateTime(selected.payout_edited_at)} · {selected.payout_edited_by || '—'}
              </div>
            )}
            <Field label="Conversion ID" mono>{selected.id}</Field>
            <Field label="Click ID" mono>{selected.click_id}</Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Conversion time">{formatDateTime(selected.created_at)}</Field>
              <Field label="Click time">{selected.clk_created_at || '—'}</Field>
              <Field label="Campaign">{campaignName(selected.tracker_campaign_id)}</Field>
              <Field label="Flow">{flowName(selected.flow_id)}</Field>
              <Field label="Offer ID" mono>{selected.offer_id || '—'}</Field>
              <Field label="Flow click ID" mono>{selected.flow_click_id || '— (legacy match)'}</Field>
              <Field label="Country">{selected.country || selected.clk_country || '—'}</Field>
              <Field label="Device">{[selected.clk_device_type, selected.clk_os, selected.clk_browser].filter(Boolean).join(' / ') || selected.platform || '—'}</Field>
              <Field label="IP" mono>{selected.ip || '—'}</Field>
              <Field label="sub_id_1" mono>{selected.clk_sub_id_1 || '—'}</Field>
            </div>
            <GoogleAdsDetails
              sub3={selected.clk_sub_id_3}
              sub4={selected.clk_sub_id_4}
              appInstanceId={selected.clk_app_instance_id}
              ga4={{ status: selected.ga4_status, eventName: selected.ga4_event_name, attempts: selected.ga4_attempts, lastError: selected.ga4_last_error }}
            />
            <AsaDetails isAsa={selected.clk_is_asa} campaignId={selected.clk_asa_campaign_id} adGroupId={selected.clk_asa_adgroup_id} keywordId={selected.clk_asa_keyword_id} />
            <div className="border-t pt-3">
              <div className="text-[11px] uppercase tracking-wide text-content-subtle mb-2">Source attribution (from click)</div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Source type" mono>{selected.clk_source_type || '—'}</Field>
                <Field label="Source bundle" mono>{selected.clk_source_bundle_id || '—'}</Field>
                <Field label="Source app" mono>{selected.clk_source_app_id || '—'}</Field>
                <Field label="Source ad" mono>{selected.clk_source_ad_id || '—'}</Field>
                <Field label="Source campaign" mono>{selected.clk_source_campaign_id || '—'}</Field>
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-content-subtle mb-1">Raw postback params</div>
              <pre className="text-xs bg-surface-muted p-3 rounded overflow-x-auto whitespace-pre-wrap break-all">
                {selected.raw_params ? prettyJson(selected.raw_params) : '—'}
              </pre>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function Field({ label, children, mono }: { label: string; children: React.ReactNode; mono?: boolean }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-content-subtle">{label}</div>
      <div className={`text-sm text-content break-all ${mono ? 'font-mono' : ''}`}>{children}</div>
    </div>
  );
}

function prettyJson(s: string) {
  try { return JSON.stringify(JSON.parse(s), null, 2); }
  catch { return s; }
}
